import React, { useEffect, useState } from 'react';
import {
  getConsent,
  hasGPCSignal,
  isBannerForceOpen,
  isLikelyEEA,
  saveConsent,
  subscribeToConsent,
} from '../lib/consent';

export default function CookieConsent() {
  const [stored, setStored] = useState(undefined);
  const [showSettings, setShowSettings] = useState(false);
  const [analytics, setAnalytics] = useState(true);
  const [ads, setAds] = useState(true);
  const [shouldShow, setShouldShow] = useState(false);

  useEffect(() => {
    setStored(getConsent());
    return subscribeToConsent(() => setStored(getConsent()));
  }, []);

  // GPC + region scoping. GPC visitors get a silent denied write; EEA visitors
  // see the banner; everyone else gets the granted default and the footer
  // "Do Not Sell or Share My Personal Information" opt-out path.
  useEffect(() => {
    if (stored === null && hasGPCSignal()) {
      saveConsent(false, false);
      return;
    }
    setShouldShow(isLikelyEEA() || isBannerForceOpen());
  }, [stored]);

  if (stored === undefined || stored !== null) return null;
  if (!shouldShow) return null;

  const baseBtn =
    'text-[11px] tracking-[0.2em] uppercase px-5 py-3 rounded-full transition-colors duration-300 font-sans';
  const primaryBtn = `${baseBtn} text-white bg-[#ba1609] hover:bg-[#8e1107]`;
  const secondaryBtn = `${baseBtn} text-[#5b4d4f] hover:text-[#ba1609] border border-black/15 hover:border-[#ba1609]`;

  return (
    <div
      role="dialog"
      aria-label="Cookie consent"
      aria-live="polite"
      className="fixed bottom-0 left-0 right-0 z-[100] bg-white border-t border-black/10 shadow-[0_-8px_30px_rgba(0,0,0,0.08)]"
      style={{ backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)' }}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-6 md:py-7">
        {!showSettings ? (
          <div className="flex flex-col lg:flex-row lg:items-center gap-6">
            <div className="flex-1">
              <p className="text-[#ba1609] text-[10px] uppercase tracking-[0.4em] font-sans mb-2">
                Privacy
              </p>
              <p className="text-[#5b4d4f] text-sm leading-relaxed font-sans max-w-[760px]">
                We use cookies to understand how visitors use this site and to deliver
                relevant marketing. You can accept all, reject non-essential, or choose
                your preferences. See our{' '}
                <a href="/privacy" className="text-[#ba1609] underline hover:no-underline">
                  privacy policy
                </a>{' '}
                for details.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 shrink-0">
              <button
                type="button"
                onClick={() => setShowSettings(true)}
                className={secondaryBtn}
              >
                Preferences
              </button>
              <button
                type="button"
                onClick={() => saveConsent(false, false)}
                className={secondaryBtn}
              >
                Reject All
              </button>
              <button
                type="button"
                onClick={() => saveConsent(true, true)}
                className={primaryBtn}
              >
                Accept All
              </button>
            </div>
          </div>
        ) : (
          <div>
            <p className="text-[#ba1609] text-[10px] uppercase tracking-[0.4em] font-sans mb-2">
              Cookie Preferences
            </p>
            <p className="text-[#5b4d4f] text-sm font-sans mb-5 max-w-[760px]">
              Choose which categories of cookies you allow. Essential cookies are required
              for the site to function and are always on.
            </p>
            <div className="space-y-0 mb-6 max-w-[760px]">
              <div className="flex items-start gap-4 py-3 border-b border-black/10">
                <div className="flex-1">
                  <p className="text-[#1a1012] text-sm font-sans">Essential</p>
                  <p className="text-[#5b4d4f] text-xs font-sans mt-1">
                    Required for the site to function correctly. Always active.
                  </p>
                </div>
                <span className="text-[#5b4d4f] text-[10px] tracking-[0.3em] uppercase font-sans mt-1">
                  Always on
                </span>
              </div>
              <label className="flex items-start gap-4 py-3 border-b border-black/10 cursor-pointer">
                <div className="flex-1">
                  <p className="text-[#1a1012] text-sm font-sans">Analytics</p>
                  <p className="text-[#5b4d4f] text-xs font-sans mt-1">
                    Helps us understand how visitors use the site (Google Analytics).
                  </p>
                </div>
                <input
                  type="checkbox"
                  checked={analytics}
                  onChange={(e) => setAnalytics(e.target.checked)}
                  className="mt-1 w-4 h-4 cursor-pointer accent-[#ba1609]"
                  aria-label="Allow analytics cookies"
                />
              </label>
              <label className="flex items-start gap-4 py-3 border-b border-black/10 cursor-pointer">
                <div className="flex-1">
                  <p className="text-[#1a1012] text-sm font-sans">Advertising</p>
                  <p className="text-[#5b4d4f] text-xs font-sans mt-1">
                    Used to measure and personalise ads.
                  </p>
                </div>
                <input
                  type="checkbox"
                  checked={ads}
                  onChange={(e) => setAds(e.target.checked)}
                  className="mt-1 w-4 h-4 cursor-pointer accent-[#ba1609]"
                  aria-label="Allow advertising cookies"
                />
              </label>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                onClick={() => setShowSettings(false)}
                className={secondaryBtn}
              >
                Back
              </button>
              <button
                type="button"
                onClick={() => saveConsent(analytics, ads)}
                className={`${primaryBtn} sm:ml-auto`}
              >
                Save Preferences
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
